import {
  Box,
  Button as ChakraButton,
  FormControl,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
} from '@chakra-ui/react';
import { mapValues, pick } from 'lodash';
import { useRef, useState } from 'react';

import { WithTooltip } from '@revelio/core';
import type { TreeItem } from '@revelio/iso-utility';
import { Button } from '@revelio/ui';

import { BreadcrumbTreeSelection } from '../../../shared/components/tree/breadcrumb-tree/BreadcrumbTreeSelection';
import type { TreeApi } from '../../../shared/components/tree/useTreeApi';
import { ListSelectIcon, StarIcon } from '../../../shared/ui/icons';
import {
  toApiKey,
  toFetchKey,
} from '../components/custom-filters/data-builder-selection-list-mapping';
import { useDataBuilderSelectionLists } from '../components/custom-filters/useDataBuilderSelectionLists';
import type { SelectionListSelectedItem } from '../data-builder.store';
import { SelectionOptionCard } from './ui/SelectionOptionCard';

type CardOption = {
  id: string;
  label: string;
  description: string;
  icon: typeof ListSelectIcon;
  enabled: boolean;
};

const DEFAULT_CARD_ID = 'select_units';
const FUTURE_RELEASE_TOOLTIP =
  'This option will be implemented in a future release';

const getCardOptions = (unitLabel: string): CardOption[] => [
  {
    id: 'select_units',
    label: `Select ${unitLabel}`,
    description: `Choose specific items for this ${unitLabel}`,
    icon: ListSelectIcon,
    enabled: true,
  },
  {
    id: 'saved_unit_lists',
    label: `Saved ${unitLabel} lists`,
    description: `Choose from your saved ${unitLabel} lists`,
    icon: StarIcon,
    enabled: false,
  },
];

type SubModalView = 'none' | 'tree_selection';

type UnitSelectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  unitLabel: string;
  selectionListKeys: string[];
  selectedItems: SelectionListSelectedItem[];
  onApply: (items: SelectionListSelectedItem[]) => void;
};

function buildInitialSelections(
  items: SelectionListSelectedItem[]
): Record<string, TreeItem> {
  const out: Record<string, TreeItem> = {};
  for (const item of items) {
    const fetchKey = toFetchKey(item.selectionListId);
    const key = `${fetchKey}.${item.id}`;
    out[key] = {
      id: item.id,
      item: { id: item.id, label: item.label },
      selectionListId: fetchKey,
      children: [],
    };
  }
  return out;
}

export const UnitSelectionModal = ({
  isOpen,
  onClose,
  unitLabel,
  selectionListKeys,
  selectedItems,
  onApply,
}: UnitSelectionModalProps) => {
  const cardOptions = getCardOptions(unitLabel);
  const [activeSubModal, setActiveSubModal] = useState<SubModalView>('none');
  const [tempSelections, setTempSelections] = useState<
    Record<string, TreeItem>
  >(() => buildInitialSelections(selectedItems));
  const [initialTreeSelections, setInitialTreeSelections] = useState<
    Record<string, TreeItem>
  >(() => buildInitialSelections(selectedItems));
  const treeRef = useRef<TreeApi>(null);

  const { selectionLists, branchLabels } = useDataBuilderSelectionLists({
    apiKeys: selectionListKeys,
  });

  const closeEntireFlow = () => {
    setActiveSubModal('none');
    onClose();
  };

  const handleCardClick = (cardId: string) => {
    if (cardId === DEFAULT_CARD_ID) {
      const initial = buildInitialSelections(selectedItems);
      setInitialTreeSelections(initial);
      setTempSelections(initial);
      setActiveSubModal('tree_selection');
    }
  };

  const handleTreeSubmit = () => {
    const items = Object.values(tempSelections)
      .filter((t): t is TreeItem => t !== null && t !== undefined)
      .map(
        (t): SelectionListSelectedItem => ({
          id: String(t.item?.id ?? t.id),
          label: String(t.item?.label ?? t.item?.longName ?? t.id),
          selectionListId: toApiKey(t.selectionListId),
        })
      );
    onApply(items);
    closeEntireFlow();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={closeEntireFlow}
        isCentered
        size="xl"
        blockScrollOnMount={false}
      >
        <ModalOverlay />
        <ModalContent data-testid="data-builder-unit-selection-modal">
          <ModalHeader color="text.primary" fontWeight="semibold">
            Select {unitLabel}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            <SimpleGrid columns={2} spacing={4}>
              {cardOptions.map((card) => (
                <WithTooltip
                  key={card.id}
                  showCondition={!card.enabled}
                  label={FUTURE_RELEASE_TOOLTIP}
                  hasArrow
                >
                  <div>
                    <SelectionOptionCard
                      key={card.id}
                      testId={`data-builder-unit-card-${card.id}`}
                      label={card.label}
                      description={card.description}
                      icon={card.icon}
                      isDisabled={!card.enabled}
                      isSelected={
                        card.id === DEFAULT_CARD_ID && selectedItems.length > 0
                      }
                      onClick={() => handleCardClick(card.id)}
                    />
                  </div>
                </WithTooltip>
              ))}
            </SimpleGrid>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isOpen && activeSubModal === 'tree_selection'}
        onClose={() => setActiveSubModal('none')}
        isCentered
        blockScrollOnMount={false}
      >
        <ModalOverlay />
        <ModalContent data-testid="data-builder-unit-tree-selection-modal">
          <ModalHeader color="text.primary" fontWeight="semibold">
            Select {unitLabel}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <FormControl>
              <Box minH="300px">
                <BreadcrumbTreeSelection
                  ref={treeRef}
                  initialSelections={initialTreeSelections}
                  branches={branchLabels}
                  selectionLists={selectionLists}
                  height={300}
                  onChange={(selections) => {
                    setTempSelections(
                      mapValues(selections, (tItem) =>
                        pick(tItem, [
                          'id',
                          'item',
                          'selectionListId',
                          'parentId',
                        ])
                      ) as Record<string, TreeItem>
                    );
                  }}
                />
              </Box>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <HStack gap={2}>
              <ChakraButton
                variant="ghost"
                size="sm"
                fontSize="12px"
                colorScheme="red"
                onClick={() => {
                  setTempSelections({});
                  treeRef.current?.clearAll();
                }}
              >
                Clear Selections
              </ChakraButton>
              <Button variant="ghost" onClick={() => setActiveSubModal('none')}>
                Back
              </Button>
              <Button color="primary" onClick={handleTreeSubmit}>
                Apply
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
